import { Component, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Canvas, useFrame, useThree } from "@react-three/fiber"
import { createPortal } from "react-dom"
import { Environment, Html, Lightformer, useGLTF, useProgress } from "@react-three/drei"
import { gsap } from "gsap"
import * as THREE from "three"

const modelUrl = "/models/iphone_17_pro.glb"
const partNames = [
  "BatteryCore",
  "ButtonsLeft",
  "ButtonsRight",
  "CameraFilters",
  "CameraFlash",
  "CameraGlass",
  "CameraLenses",
  "CameraPlate",
  "CameraRings",
  "DisplayAssembly",
  "RearGlassAssembly",
] as const

type PartName = typeof partNames[number]

type Pose = {
  position: [number, number, number]
  rotation: [number, number, number]
  scale: number
}

const desktopPoses: Pose[] = [
  { position: [1.45, -0.05, 0], rotation: [0.08, 0.42, 0.08], scale: 18 },
  { position: [1.52, -0.05, 0], rotation: [0, Math.PI - 0.12, 0], scale: 18 },
  { position: [-1.52, -0.16, 0], rotation: [-0.08, 0.58, -0.04], scale: 16.5 },
  { position: [1.55, 0, 0], rotation: [-0.04, Math.PI / 2.08, 0.06], scale: 17 },
  { position: [-1.52, 0.08, 0], rotation: [-0.16, 0.9, -0.04], scale: 17 },
  { position: [1.55, 0.15, 0], rotation: [0, 0.38, 0], scale: 16 },
]

const mobilePoses: Pose[] = [
  { position: [0, -0.82, 0], rotation: [0.08, 0.38, 0.08], scale: 11.5 },
  { position: [0.12, 0.76, 0], rotation: [0, Math.PI - 0.08, 0], scale: 10.8 },
  { position: [0, 0.74, 0], rotation: [-0.08, 0.48, 0], scale: 11 },
  { position: [0, 0.76, 0], rotation: [0, Math.PI / 2.12, 0.05], scale: 10.5 },
  { position: [0, 0.78, 0], rotation: [-0.14, 0.74, 0], scale: 10.8 },
  { position: [0, 0.74, 0], rotation: [0, 0.34, 0], scale: 10.6 },
]

const inspectionRotationOffsets: Array<[number, number, number]> = [
  [-0.015, -0.62, -0.025],
  [-0.02, 0.32, 0.025],
  [0.025, -0.48, -0.035],
  [-0.02, 0.18, 0.025],
]

function PhoneModel({ reducedMotion, onReady }: { reducedMotion: boolean; onReady: () => void }) {
  const gltf = useGLTF(modelUrl)
  const { scene, reactiveMaterials, explodedParts, batteryMaterials, cameraMaterials } = useMemo(() => {
    const scene = gltf.scene.clone(true)
    const explodedParts = {} as Partial<Record<PartName, THREE.Object3D>>
    const batteryMaterials: THREE.MeshStandardMaterial[] = []
    const cameraMaterials: Array<{
      material: THREE.MeshStandardMaterial
      color: THREE.Color
      emissive: THREE.Color
      emissiveIntensity: number
    }> = []
    const reactiveMaterials: Array<{
      material: THREE.MeshStandardMaterial
      color: THREE.Color
      emissive: THREE.Color
    }> = []

    scene.traverse((object) => {
      if ((partNames as readonly string[]).includes(object.name)) {
        explodedParts[object.name as PartName] = object
      }
      if (!(object instanceof THREE.Mesh)) return
      const sourceMaterials = Array.isArray(object.material) ? object.material : [object.material]
      const clonedMaterials = sourceMaterials.map((source) => source.clone())
      object.material = Array.isArray(object.material) ? clonedMaterials : clonedMaterials[0]

      clonedMaterials.forEach((material) => {
        if (!(material instanceof THREE.MeshStandardMaterial)) return
        if (/battery cyan core/i.test(material.name)) {
          material.transparent = true
          material.opacity = 0
          material.depthWrite = false
          batteryMaterials.push(material)
        }
        const inCameraAssembly = (() => {
          let parent: THREE.Object3D | null = object
          while (parent) {
            if (/^Camera(?:Lenses|Filters|Glass|Rings|Plate|Flash)$/.test(parent.name)) return true
            parent = parent.parent
          }
          return false
        })()
        if (inCameraAssembly && material.color.getHSL({ h: 0, s: 0, l: 0 }).l < .34) {
          cameraMaterials.push({
            material,
            color: material.color.clone(),
            emissive: material.emissive.clone(),
            emissiveIntensity: material.emissiveIntensity,
          })
        }
        if (!/anodized aluminum|display frame/i.test(material.name)) return
        reactiveMaterials.push({
          material,
          color: material.color.clone(),
          emissive: material.emissive.clone(),
        })
      })
    })

    return { scene, reactiveMaterials, explodedParts, batteryMaterials, cameraMaterials }
  }, [gltf.scene])
  const group = useRef<THREE.Group>(null)
  const interaction = useRef<THREE.Group>(null)
  const halo = useRef<THREE.Group>(null)
  const haloMaterial = useRef<THREE.MeshBasicMaterial>(null)
  const keyLight = useRef<THREE.DirectionalLight>(null)
  const rimLight = useRef<THREE.SpotLight>(null)
  const hoverLight = useRef<THREE.PointLight>(null)
  const chapters = useRef<HTMLElement[]>([])
  const explodedPartsRef = useRef<Partial<Record<PartName, THREE.Object3D>>>({})
  const partBase = useRef<Partial<Record<PartName, {
    position: THREE.Vector3
    rotation: THREE.Euler
    scale: THREE.Vector3
  }>>>({})
  const hovering = useRef(false)
  const touchReset = useRef<number | null>(null)
  const { camera, gl, size } = useThree()

  useEffect(() => {
    chapters.current = [
      ...document.querySelectorAll<HTMLElement>(".service-panel"),
      document.querySelector<HTMLElement>(".process"),
    ].filter((element): element is HTMLElement => Boolean(element))
  }, [])

  useEffect(() => {
    explodedPartsRef.current = explodedParts
    partNames.forEach((name) => {
      const part = explodedParts[name]
      if (!part) return
      partBase.current[name] = {
        position: part.position.clone(),
        rotation: part.rotation.clone(),
        scale: part.scale.clone(),
      }
    })
  }, [explodedParts])

  useFrame((_, delta) => {
    const groupNode = group.current
    const haloNode = halo.current
    const parts = explodedPartsRef.current
    if (!groupNode || !haloNode || chapters.current.length === 0) return

    const poses = size.width < 900 ? mobilePoses : desktopPoses
    const viewportHeight = window.innerHeight
    const headerHeight = size.width < 900 ? 70 : 82
    let fromIndex = 0
    let toIndex = 0
    let progress = 0

    for (let index = 0; index < chapters.current.length; index += 1) {
      const top = chapters.current[index].getBoundingClientRect().top
      const entryProgress = THREE.MathUtils.clamp((viewportHeight - top) / Math.max(viewportHeight - headerHeight, 1), 0, 1)
      if (entryProgress <= 0) break
      fromIndex = index
      toIndex = index + 1
      progress = entryProgress
      if (entryProgress < 1) break
    }

    const smoothStep = (value: number) => value * value * (3 - 2 * value)
    const chapterProgress = (chapterIndex: number) => {
      const element = chapters.current[chapterIndex]
      if (!element) return 0
      const bounds = element.getBoundingClientRect()
      const stickyViewportHeight = viewportHeight - headerHeight
      const travel = Math.max(bounds.height - stickyViewportHeight, 1)
      return THREE.MathUtils.clamp((headerHeight - bounds.top) / travel, 0, 1)
    }
    const inspectionEnvelope = (chapterIndex: number) => {
      if (reducedMotion) return 0
      const timelineProgress = chapterProgress(chapterIndex)
      if (timelineProgress < .16) return 0
      if (timelineProgress < .38) return smoothStep((timelineProgress - .16) / .22)
      if (timelineProgress <= .56) return 1
      if (timelineProgress < .82) return 1 - smoothStep((timelineProgress - .56) / .26)
      return 0
    }
    const inspectionAmounts = inspectionRotationOffsets.map((_, index) => inspectionEnvelope(index))
    const eased = progress * progress * (3 - 2 * progress)
    const from = poses[Math.min(fromIndex, poses.length - 1)]
    const to = poses[Math.min(toIndex, poses.length - 1)]
    const targetX = THREE.MathUtils.lerp(from.position[0], to.position[0], eased)
    const targetY = THREE.MathUtils.lerp(from.position[1], to.position[1], eased)
    const targetZ = THREE.MathUtils.lerp(from.position[2], to.position[2], eased)
    let targetRotationX = THREE.MathUtils.lerp(from.rotation[0], to.rotation[0], eased)
    let targetRotationY = THREE.MathUtils.lerp(from.rotation[1], to.rotation[1], eased)
    let targetRotationZ = THREE.MathUtils.lerp(from.rotation[2], to.rotation[2], eased)
    inspectionAmounts.forEach((amount, index) => {
      const offset = inspectionRotationOffsets[index]
      targetRotationX += offset[0] * amount
      targetRotationY += offset[1] * amount
      targetRotationZ += offset[2] * amount
    })
    const targetScale = THREE.MathUtils.lerp(from.scale, to.scale, eased)
    const damping = reducedMotion ? 100 : 6.2

    groupNode.position.x = THREE.MathUtils.damp(groupNode.position.x, targetX, damping, delta)
    groupNode.position.y = THREE.MathUtils.damp(groupNode.position.y, targetY, damping, delta)
    groupNode.position.z = THREE.MathUtils.damp(groupNode.position.z, targetZ, damping, delta)
    groupNode.rotation.x = THREE.MathUtils.damp(groupNode.rotation.x, targetRotationX, damping, delta)
    groupNode.rotation.y = THREE.MathUtils.damp(groupNode.rotation.y, targetRotationY, damping, delta)
    groupNode.rotation.z = THREE.MathUtils.damp(groupNode.rotation.z, targetRotationZ, damping, delta)
    const scale = THREE.MathUtils.damp(groupNode.scale.x, targetScale, damping, delta)
    groupNode.scale.setScalar(scale)

    haloNode.position.x = THREE.MathUtils.damp(haloNode.position.x, targetX, damping * .9, delta)
    haloNode.position.y = THREE.MathUtils.damp(haloNode.position.y, targetY, damping * .9, delta)
    const haloPulse = 1 + Math.sin(eased * Math.PI) * .055
    const haloScale = THREE.MathUtils.damp(haloNode.scale.x, haloPulse, damping * .75, delta)
    haloNode.scale.set(haloScale, haloScale, 1)

    if (haloMaterial.current) {
      haloMaterial.current.opacity = THREE.MathUtils.damp(haloMaterial.current.opacity, .14 + Math.sin(eased * Math.PI) * .12, damping, delta)
    }
    if (keyLight.current) {
      keyLight.current.intensity = THREE.MathUtils.damp(keyLight.current.intensity, toIndex === 2 ? 1.3 : 1.75, damping, delta)
    }
    if (rimLight.current) {
      rimLight.current.intensity = THREE.MathUtils.damp(rimLight.current.intensity, toIndex === 3 ? 5.1 : 3.2, damping, delta)
    }

    const [screenAmount, cameraAmount, structureAmount, batteryAmount] = inspectionAmounts
    const partDamping = reducedMotion ? 100 : 7.4
    const movePart = (name: PartName, offset: [number, number, number], amount: number) => {
      const part = parts[name]
      const base = partBase.current[name]
      if (!part || !base) return
      part.position.x = THREE.MathUtils.damp(part.position.x, base.position.x + offset[0] * amount, partDamping, delta)
      part.position.y = THREE.MathUtils.damp(part.position.y, base.position.y + offset[1] * amount, partDamping, delta)
      part.position.z = THREE.MathUtils.damp(part.position.z, base.position.z + offset[2] * amount, partDamping, delta)
    }

    movePart("DisplayAssembly", [0, 0, -.062], screenAmount)
    const display = parts.DisplayAssembly
    const displayBase = partBase.current.DisplayAssembly
    if (display && displayBase) {
      display.rotation.x = THREE.MathUtils.damp(display.rotation.x, displayBase.rotation.x + .045 * screenAmount, partDamping, delta)
    }

    movePart("CameraPlate", [0, 0, .006], cameraAmount)
    movePart("CameraRings", [0, 0, .016], cameraAmount)
    movePart("CameraFilters", [0, 0, .026], cameraAmount)
    movePart("CameraLenses", [0, 0, .038], cameraAmount)
    movePart("CameraGlass", [0, 0, .052], cameraAmount)
    movePart("CameraFlash", [0, 0, .02], cameraAmount)

    const cameraAccent = new THREE.Color("#15444b")
    const cameraEmission = new THREE.Color("#05282e")
    cameraMaterials.forEach(({ material, color, emissive, emissiveIntensity }) => {
      material.color.copy(color).lerp(cameraAccent, cameraAmount)
      material.emissive.copy(emissive).lerp(cameraEmission, cameraAmount)
      material.emissiveIntensity = THREE.MathUtils.lerp(emissiveIntensity, .55, cameraAmount)
    })

    movePart("ButtonsLeft", [-.022, 0, 0], structureAmount)
    movePart("ButtonsRight", [.022, 0, 0], structureAmount)
    const flash = parts.CameraFlash
    const flashBase = partBase.current.CameraFlash
    if (flash && flashBase) {
      const flashScale = THREE.MathUtils.lerp(1, 1.12, structureAmount)
      flash.scale.set(
        flashBase.scale.x * flashScale,
        flashBase.scale.y * flashScale,
        flashBase.scale.z * flashScale,
      )
    }

    movePart("RearGlassAssembly", [0, 0, .04], batteryAmount)
    movePart("BatteryCore", [0, 0, .018], batteryAmount)
    batteryMaterials.forEach((material) => {
      material.opacity = THREE.MathUtils.damp(material.opacity, batteryAmount, partDamping, delta)
      material.emissiveIntensity = THREE.MathUtils.damp(material.emissiveIntensity, THREE.MathUtils.lerp(.7, 2.4, batteryAmount), partDamping, delta)
    })
  })

  useEffect(() => {
    const firstFrame = requestAnimationFrame(() => requestAnimationFrame(onReady))
    return () => cancelAnimationFrame(firstFrame)
  }, [onReady])

  useEffect(() => {
    const groupNode = group.current
    const interactionNode = interaction.current
    const haloNode = haloMaterial.current
    const rimNode = rimLight.current
    const hoverLightNode = hoverLight.current
    if (!groupNode || !interactionNode || !haloNode || !rimNode || !hoverLightNode) return

    const raycaster = new THREE.Raycaster()
    const pointer = new THREE.Vector2()
    const cyan = new THREE.Color("#2f8994")
    const cyanEmission = new THREE.Color("#06272d")

    const setHover = (active: boolean, pointerX = 0, pointerY = 0) => {
      const duration = reducedMotion ? 0.15 : 0.48
      hovering.current = active

      reactiveMaterials.forEach(({ material, color, emissive }) => {
        const nextColor = active ? cyan : color
        const nextEmissive = active ? cyanEmission : emissive
        gsap.to(material.color, { r: nextColor.r, g: nextColor.g, b: nextColor.b, duration, ease: "power2.out", overwrite: "auto" })
        gsap.to(material.emissive, { r: nextEmissive.r, g: nextEmissive.g, b: nextEmissive.b, duration, ease: "power2.out", overwrite: "auto" })
        gsap.to(material, { emissiveIntensity: active ? 0.34 : 1, duration, ease: "power2.out", overwrite: "auto" })
      })

      gsap.to(interactionNode.scale, {
        x: active && !reducedMotion ? 1.025 : 1,
        y: active && !reducedMotion ? 1.025 : 1,
        z: active && !reducedMotion ? 1.025 : 1,
        duration,
        ease: "power3.out",
        overwrite: "auto",
      })
      gsap.to(interactionNode.rotation, {
        x: active && !reducedMotion ? -pointerY * 0.045 : 0,
        y: active && !reducedMotion ? pointerX * 0.055 : 0,
        z: active && !reducedMotion ? -pointerX * 0.018 : 0,
        duration: active ? 0.3 : 0.6,
        ease: "power3.out",
        overwrite: "auto",
      })
      gsap.to(haloNode, { opacity: active ? 0.34 : 0.14, duration, overwrite: "auto" })
      gsap.to(rimNode, { intensity: active ? 6.2 : 3.2, duration, overwrite: "auto" })
      gsap.to(hoverLightNode, { intensity: active ? 2.8 : 0, duration, overwrite: "auto" })
    }

    const hitTest = (event: PointerEvent) => {
      const rect = gl.domElement.getBoundingClientRect()
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      )
      groupNode.updateWorldMatrix(true, true)
      raycaster.setFromCamera(pointer, camera)
      return raycaster.intersectObject(groupNode, true).length > 0
    }

    const move = (event: PointerEvent) => {
      if (event.pointerType === "touch") return
      const active = hitTest(event)
      if (active || hovering.current) setHover(active, pointer.x, pointer.y)
      if (active) {
        gsap.to(hoverLightNode.position, {
          x: pointer.x * 2.4,
          y: pointer.y * 1.7,
          z: 2.4,
          duration: 0.22,
          ease: "power2.out",
          overwrite: "auto",
        })
      }
    }

    const touch = (event: PointerEvent) => {
      if (event.pointerType !== "touch" || !hitTest(event)) return
      setHover(true, pointer.x, pointer.y)
      if (touchReset.current) window.clearTimeout(touchReset.current)
      touchReset.current = window.setTimeout(() => setHover(false), 850)
    }

    const leave = () => hovering.current && setHover(false)
    window.addEventListener("pointermove", move, { passive: true })
    window.addEventListener("pointerdown", touch, { passive: true })
    document.documentElement.addEventListener("mouseleave", leave)
    window.addEventListener("blur", leave)

    return () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerdown", touch)
      document.documentElement.removeEventListener("mouseleave", leave)
      window.removeEventListener("blur", leave)
      if (touchReset.current) window.clearTimeout(touchReset.current)
      reactiveMaterials.forEach(({ material }) => gsap.killTweensOf([material, material.color, material.emissive]))
      gsap.killTweensOf([interactionNode.scale, interactionNode.rotation, haloNode, rimNode, hoverLightNode])
    }
  }, [camera, gl, reactiveMaterials, reducedMotion])

  useLayoutEffect(() => {
    if (!group.current) return
    const poses = size.width < 900 ? mobilePoses : desktopPoses
    const first = poses[0]
    group.current.position.set(...first.position)
    group.current.rotation.set(...first.rotation)
    group.current.scale.setScalar(first.scale)
    halo.current?.position.set(first.position[0], first.position[1], -0.7)

  }, [size.width])

  return (
    <>
      <group ref={halo} position={[firstPosition(size.width), 0, -0.7]}>
        <mesh position={[0, 0, -0.015]}>
          <circleGeometry args={[1.13, 96]} />
          <meshBasicMaterial color="#123238" transparent opacity={0.16} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh>
          <ringGeometry args={[1.16, 1.18, 96]} />
          <meshBasicMaterial ref={haloMaterial} color="#65f5ff" transparent opacity={0.14} depthWrite={false} toneMapped={false} />
        </mesh>
      </group>
      <group ref={group}>
        <group ref={interaction}>
          <primitive object={scene} />
        </group>
      </group>
      <hemisphereLight args={["#dce8e9", "#050708", 0.34]} />
      <directionalLight ref={keyLight} position={[3.5, 4, 5]} intensity={1.75} color="#e8eeee" />
      <spotLight ref={rimLight} position={[-4, 1, 3]} intensity={3.2} color="#65f5ff" angle={0.42} penumbra={0.85} distance={12} />
      <pointLight ref={hoverLight} position={[1.8, 0.4, 2.4]} intensity={0} color="#65f5ff" distance={5.5} decay={2} />
      <pointLight position={[2, -3, 2]} intensity={0.65} color="#94a2a5" distance={9} />
    </>
  )
}

function firstPosition(width: number) {
  return width < 900 ? mobilePoses[0].position[0] : desktopPoses[0].position[0]
}

function ExperienceLoader({ done }: { done: boolean }) {
  const { progress } = useProgress()
  const [visible, setVisible] = useState(true)

  if (!visible) return null

  return createPortal(
    <div className={`experience-loader ${done ? "experience-loader--done" : ""}`} onTransitionEnd={() => done && setVisible(false)}>
      <div className="experience-loader__brand">
        <img src="/logo equipe cell sem fundo.png" alt="" />
        <span>Preparando a experiência</span>
      </div>
      <div className="experience-loader__status">
        <span>{Math.round(progress).toString().padStart(2, "0")}</span>
        <div><i style={{ transform: `scaleX(${Math.max(progress, 4) / 100})` }} /></div>
        <small>Carregando aparelho</small>
      </div>
    </div>,
    document.body,
  )
}

class CanvasBoundary extends Component<{ children: ReactNode; onError: () => void }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch() {
    this.props.onError()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

function PhoneFallback() {
  return (
    <div className="phone-fallback" aria-hidden="true">
      <div className="phone-fallback__camera"><i /><i /><i /></div>
      <div className="phone-fallback__mark">EQUIPE CELL</div>
    </div>
  )
}

export function PhoneStage() {
  const [failed, setFailed] = useState(false)
  const [sceneReady, setSceneReady] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const update = () => setReducedMotion(query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])

  useEffect(() => {
    document.body.style.overflow = sceneReady ? "" : "hidden"
    const safety = window.setTimeout(() => setSceneReady(true), 8000)
    return () => {
      window.clearTimeout(safety)
      document.body.style.overflow = ""
    }
  }, [sceneReady])

  const handleFailure = () => {
    setFailed(true)
    setSceneReady(true)
  }

  return (
    <div className={`phone-stage ${failed ? "phone-stage--failed" : ""}`}>
      <ExperienceLoader done={sceneReady} />
      <PhoneFallback />
      {!failed && (
        <CanvasBoundary onError={handleFailure}>
          <Canvas
            dpr={[1, 1.65]}
            camera={{ position: [0, 0, 5.2], fov: 36, near: 0.1, far: 100 }}
            gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}
            onCreated={({ gl }) => {
              gl.toneMapping = THREE.ACESFilmicToneMapping
              gl.toneMappingExposure = 0.72
            }}
          >
            <Suspense fallback={<Html center><span className="model-loader">Preparando aparelho</span></Html>}>
              <PhoneModel reducedMotion={reducedMotion} onReady={() => setSceneReady(true)} />
              <Environment resolution={96} environmentIntensity={0.28}>
                <Lightformer form="rect" intensity={1.4} color="#e7eeee" position={[0, 3, -3]} scale={[5, 2, 1]} />
                <Lightformer form="rect" intensity={2.8} color="#65f5ff" position={[-3, 0, 1]} rotation={[0, Math.PI / 2, 0]} scale={[1, 4, 1]} />
              </Environment>
            </Suspense>
          </Canvas>
        </CanvasBoundary>
      )}
    </div>
  )
}

useGLTF.preload(modelUrl)
